/* eslint-disable sort-keys */
/* eslint-disable sort-vars */
import React from 'react';
import { observer } from 'mobx-react';
import { Alert, Skeleton as _Skeleton } from '@material-ui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
} from '@material-ui/core';
import AlertIcon from '@material-ui/icons/NotificationsActive';
import CheckInIcon from '@material-ui/icons/TouchApp';
import PhoneIcon from '@material-ui/icons/Phone';
import SettingsIcon from '@material-ui/icons/Settings';
import StopMonitoringIcon from '@material-ui/icons/AlarmOff';
import WarningIcon from '@material-ui/icons/Warning';
import { destroy, post, put } from '@lib/ApiClient';
import { OperatingRoom } from '@types';
import store from '@lib/store';
import useSubmenus from '@lib/useSubmenu';
import { EditAlerts } from '@component/pages';
import BigListItem from '@component/BigListItem';
import Confirm from '@component/Confirm';
import Dialog from '@component/Dialog';
import { Box } from '@atoms';

export default observer(function OperatingRoomMenu({
  room,
  open,
  onClose,
}: {
  room: OperatingRoom
  open: boolean
  onClose: () => void
}) {
  const [problem, setProblem] = React.useState<string | null>(null);

  const monitor = store.monitorForRoom(room),
    { alerts, configuringVital } = monitor,
    // EVERY open alarm, not alerts[0]. A room can hold one alarm per vital —
    // maybe_create_alert dedups by vital_name, so heart rate and oxygen breach
    // independently and both stay open. Showing only the first meant the
    // clinician cleared what they could see, the room stayed red, and the
    // second alarm was neither visible nor reachable. Verified on a live room:
    // "Heart rate: 190" and "Oxygen: 80" open together, only the first shown.
    openAlerts = alerts ?? [],
    hasAlert = openAlerts.length > 0,
    { submenu, makeSubmenuSetter, closeSubmenu } = useSubmenus({
      names: ['alerts', 'confirmingCheckIn', 'confirmingRemoveMonitor'],
      defaultOpen: configuringVital ? 'alerts' : undefined,
    }),
    clearAlert = async function (reportedFalse = false) {
      setProblem(null);

      // Snapshot: act on exactly the alarms currently on screen, so a new one
      // arriving mid-tap is not cleared unseen.
      const targets = openAlerts.slice();

      try {
        await Promise.all(
          targets.map(alert =>
            put(`alerts/${alert.id}`, {
              clearedAt: new Date().toISOString(),
              reportedFalse,
            }),
          ),
        );
        onClose();
      } catch (e) {
        // Previously unhandled: the request failed, the dialog closed anyway,
        // and the clinician was left believing a live alarm had been dealt
        // with. Saying nothing is the worst option here.
        setProblem('Could not clear the alarm — it is still active. Please try again.');
      }
    },
    content = hasAlert ? (
      <Box fullWidth column>
        {openAlerts.map(alert => (
          <Alert key={alert.id} severity="warning">
            {alert.message}
          </Alert>
        ))}
        {problem ? <Alert severity="error">{problem}</Alert> : null}
        <List>
          <BigListItem
            button
            component="a"
            href={`tel:${room.phone}`}
            LeftIcon={PhoneIcon}
          >
            Call OR
          </BigListItem>
          <BigListItem
            button
            onClick={async () => {
              await clearAlert();
            }}
            LeftIcon={StopMonitoringIcon}
          >
            {openAlerts.length > 1 ? `Clear all ${openAlerts.length}` : 'Clear'}
          </BigListItem>
          <BigListItem
            button
            onClick={async () => {
              await clearAlert(true);
            }}
            LeftIcon={WarningIcon}
          >
            {openAlerts.length > 1 ? 'Report all false' : 'Report false'}
          </BigListItem>
        </List>
      </Box>
    ) : (
      <>
        {problem ? <Alert severity="error">{problem}</Alert> : null}
        <List>
          <BigListItem
            button
            onClick={makeSubmenuSetter('alerts')}
            LeftIcon={SettingsIcon}
          >
            Configure Alerts
          </BigListItem>
          <BigListItem
            button
            onClick={makeSubmenuSetter('confirmingCheckIn')}
            LeftIcon={CheckInIcon}
          >
            Check In
          </BigListItem>
          <BigListItem
            button
            onClick={makeSubmenuSetter('confirmingRemoveMonitor')}
            LeftIcon={StopMonitoringIcon}
          >
            Stop Monitoring
          </BigListItem>
          {store.currentOrganization.features.demo ? (
            <>
              <BigListItem
                button
                onClick={async () => {
                  try {
                    Object.assign(
                      room,
                      await put(`operating_rooms/${room.id}`, {
                        demoCondition:
                          room.demoCondition === 'crash_pulse'
                            ? 'ok'
                            : 'crash_pulse',
                      }),
                    );
                    onClose();
                  } catch (e) {
                    setProblem('Could not change the demo condition.');
                  }
                }}
                LeftIcon={AlertIcon}
              >
                DEMO: {room.demoCondition === 'crash_pulse' ? 'Clear ' : ''}Heart
                Rate Crash
              </BigListItem>
              <BigListItem
                button
                onClick={async () => {
                  try {
                    Object.assign(
                      room,
                      await put(`operating_rooms/${room.id}`, {
                        demoCondition:
                          room.demoCondition === 'crash_o2' ? 'ok' : 'crash_o2',
                      }),
                    );
                    onClose();
                  } catch (e) {
                    setProblem('Could not change the demo condition.');
                  }
                }}
                LeftIcon={AlertIcon}
              >
                DEMO: {room.demoCondition === 'crash_o2' ? 'Clear ' : ''}O2 Crash
              </BigListItem>
            </>
          ) : null}
        </List>
      </>
    );

  return (
    <>
      <Confirm
        open={submenu === 'confirmingRemoveMonitor'}
        title={'Remove Monitor'}
        content={() => `Stop monitoring ${room.name}?`}
        onConfirm={async () => {
          // See ImpersonateUser: store.saving is global, so leaking it true
          // disables every save button in the app.
          store.saving = true;

          try {
            await destroy(`monitors/${monitor.id}`);
            store.monitors.remove(monitor);
            onClose();
          } finally {
            store.saving = false;
          }
        }}
        onCancel={closeSubmenu}
      />
      <Confirm
        open={submenu === 'confirmingCheckIn'}
        title={'Check In'}
        content={() => `Confirm you are at ${room.name}`}
        onConfirm={async () => {
          try {
            await post('in_person_checks', { monitorId: monitor.id });
            closeSubmenu();
          } catch (e) {
            // The check resets a 90-minute timer. Failing silently left the
            // clinician believing they had recorded a visit they had not.
            setProblem('Could not record the check-in. Please try again.');
            closeSubmenu();
          }
        }}
        onCancel={closeSubmenu}
      />
      <EditAlerts
        open={submenu === 'alerts'}
        onClose={() => {
          monitor.configuringVital = null;
          closeSubmenu();
        }}
        monitor={monitor}
      />
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {room.name} - {store.facilityForRoom(room).name}
        </DialogTitle>
        <DialogContent>
          <Box fontSize="125%" fill>
            {content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
